import React, { useState } from 'react';
import { useRotaForm } from '../context/RotaContext';
import { FaSearch, FaUserPlus, FaFilter } from 'react-icons/fa';

const Step2StaffSelection = () => {
  const { formData, updateField } = useRotaForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All Roles');
  const [statusFilter, setStatusFilter] = useState('All Statuses');

  // Sample staff list matching the criteria from the design interface
  const staffList = [
    { id: '1', name: 'Ahmed Raza', role: 'Head Chef', department: 'Kitchen', type: 'Full-time', status: 'Active', initials: 'AR' },
    { id: '2', name: 'Sara Khan', role: 'Server', department: 'Front of House', type: 'Part-time', status: 'Active', initials: 'SK' },
    { id: '3', name: 'John Smith', role: 'Kitchen Assistant', department: 'Kitchen', type: 'Full-time', status: 'Active', initials: 'JS' },
    { id: '4', name: 'Emily Davis', role: 'Manager', department: 'Operations', type: 'Full-time', status: 'Active', initials: 'ED' },
    { id: '5', name: 'Michael Chang', role: 'Cleaner', department: 'Facilities', type: 'Part-time', status: 'Active', initials: 'MC' }
  ];

  // Handle single row checkbox toggle
  const handleSelectStaff = (staffId) => {
    const currentSelected = [...formData.selectedStaffIds];
    if (currentSelected.includes(staffId)) {
      updateField('selectedStaffIds', currentSelected.filter(id => id !== staffId));
    } else {
      updateField('selectedStaffIds', [...currentSelected, staffId]);
    }
  };

  // Handle "Select All" checkbox toggle
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredStaff.map(staff => staff.id);
      updateField('selectedStaffIds', allIds);
    } else {
      updateField('selectedStaffIds', []);
    }
  };

  // Filter logic based on user search term inputs
  const filteredStaff = staffList.filter(staff => {
    const matchesSearch = staff.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          staff.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'All Roles' || staff.role === roleFilter;
    const matchesStatus = statusFilter === 'All Statuses' || staff.status === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      {/* Section Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-blue-600 flex items-center justify-center text-white font-bold">2</div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Staff Selection</h2>
            <p className="text-xs text-gray-500">Select which staff members will be included or available for assignment in this rota timeline.</p>
          </div>
        </div>
        <button type="button" className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium transition shadow-sm">
          <FaUserPlus size={12} /> Add Staff
        </button>
      </div>

      {/* Filter and Control Subbar Toolbar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="relative">
          <FaSearch className="absolute left-3 top-2.5 text-gray-400 text-xs" />
          <input 
            type="text" 
            placeholder="Search staff or roles..." 
            className="w-full text-xs pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div>
          <select 
            className="w-full text-xs p-2 border border-gray-300 rounded-md bg-white focus:outline-none"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option>All Roles</option>
            <option>Head Chef</option>
            <option>Server</option>
            <option>Kitchen Assistant</option>
            <option>Manager</option>
            <option>Cleaner</option>
          </select>
        </div>

        <div>
          <select className="w-full text-xs p-2 border border-gray-300 rounded-md bg-white focus:outline-none">
            <option>All Departments</option>
            <option>Kitchen</option>
            <option>Front of House</option>
            <option>Operations</option>
          </select>
        </div>

        <div>
          <select 
            className="w-full text-xs p-2 border border-gray-300 rounded-md bg-white focus:outline-none"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option>All Statuses</option>
            <option>Active</option>
            <option>On Leave</option>
          </select>
        </div>
      </div>

      {/* Data Layout Table UI */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-left text-xs">
          <thead className="bg-slate-50 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            <tr>
              <th className="p-3 w-12 text-center">
                <input 
                  type="checkbox" 
                  className="rounded text-blue-600 focus:ring-blue-500 w-3.5 h-3.5"
                  onChange={handleSelectAll}
                  checked={filteredStaff.length > 0 && filteredStaff.every(staff => formData.selectedStaffIds.includes(staff.id))}
                />
              </th>
              <th className="p-3">Name</th>
              <th className="p-3">Role</th>
              <th className="p-3">Department</th>
              <th className="p-3">Employment Type</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 text-gray-700">
            {filteredStaff.map((staff) => {
              const isChecked = formData.selectedStaffIds.includes(staff.id);
              return (
                <tr key={staff.id} className={`hover:bg-slate-50 transition ${isChecked ? 'bg-blue-50/30' : ''}`}>
                  <td className="p-3 text-center">
                    <input 
                      type="checkbox" 
                      className="rounded text-blue-600 focus:ring-blue-500 w-3.5 h-3.5"
                      checked={isChecked}
                      onChange={() => handleSelectStaff(staff.id)}
                    />
                  </td>
                  <td className="p-3 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 font-bold flex items-center justify-center text-[10px]">
                        {staff.initials}
                      </div>
                      <span>{staff.name}</span>
                    </div>
                  </td>
                  <td className="p-3">{staff.role}</td>
                  <td className="p-3">{staff.department}</td>
                  <td className="p-3">{staff.type}</td>
                  <td className="p-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {staff.status}
                    </span>
                  </td>
                </tr>
              );
            })}
            {filteredStaff.length === 0 && (
              <tr>
                <td colSpan="6" className="p-8 text-center text-gray-400 italic">
                  No staff profiles found matching the current filter configurations.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Step2StaffSelection;
